class NotificationService {
  constructor() {
    this.audioContext = null;
    this.notificationInterval = null;
    this.soundEnabled = false;
    this.volume = 50;
    this.audioPermission = 'prompt';
    this.pendingOrders = 0;
    this.listeners = new Set();
    this.orderMonitoringStarted = false;
    this.realtimeSubscription = null;
    this.checkInterval = null;
    this.selectedSound = 'beep'; // Default to original beep
    this.audioElements = {}; // Cache for audio elements
    this.isPageVisible = true; // Track page visibility
    
    // Setup page visibility listener for background notifications
    this.setupPageVisibilityListener();
    
    // Available sound options
    this.soundOptions = [
      { id: 'beep', name: 'Standaard Beep', type: 'generated' },
    ];
    
    // Load settings on startup
    this.loadSettings();
    
    
    // Start monitoring immediately
    this.startOrderMonitoring();
  }

  static getInstance() {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // Add listener for state changes
  addListener(callback) {
    this.listeners.add(callback);
    // Immediately call with current state
    callback({
      soundEnabled: this.soundEnabled,
      volume: this.volume,
      audioPermission: this.audioPermission,
      pendingOrders: this.pendingOrders,
      selectedSound: this.selectedSound,
      soundOptions: this.soundOptions
    });
  }

  // Remove listener
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  // Notify all listeners of state change
  notifyListeners() {
    const state = {
      soundEnabled: this.soundEnabled,
      volume: this.volume,
      audioPermission: this.audioPermission,
      pendingOrders: this.pendingOrders,
      selectedSound: this.selectedSound,
      soundOptions: this.soundOptions
    };
    this.listeners.forEach(callback => callback(state));
  }

  loadSettings() {
    const savedSoundEnabled = localStorage.getItem('kmfc-sound-enabled');
    const savedVolume = localStorage.getItem('kmfc-sound-volume');
    const savedPermission = localStorage.getItem('kmfc-audio-permission');
    const savedSelectedSound = localStorage.getItem('kmfc-selected-sound');
    
    
    // Only enable sound if we have permission
    if (savedSoundEnabled !== null && savedPermission === 'granted') {
      this.soundEnabled = JSON.parse(savedSoundEnabled);
    }
    if (savedVolume !== null) {
      this.volume = parseInt(savedVolume);
    }
    if (savedPermission !== null) {
      this.audioPermission = savedPermission;
    }
    if (savedSelectedSound !== null) {
      this.selectedSound = savedSelectedSound;
    }
    
  }

  saveSettings() {
    localStorage.setItem('kmfc-sound-enabled', JSON.stringify(this.soundEnabled));
    localStorage.setItem('kmfc-sound-volume', this.volume.toString());
    localStorage.setItem('kmfc-audio-permission', this.audioPermission);
    localStorage.setItem('kmfc-selected-sound', this.selectedSound);
  }

  async requestAudioPermission() {
    try {
      // Try to create AudioContext - this requires user interaction
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      // Test if we can play audio
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      gainNode.gain.setValueAtTime(0, ctx.currentTime); // Silent test
      oscillator.frequency.setValueAtTime(440, ctx.currentTime);
      
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.01);
      
      this.audioContext = ctx;
      this.audioPermission = 'granted';
      this.saveSettings();
      this.notifyListeners();
      
      return true;
    } catch (error) {
      console.error('Audio permission denied:', error);
      this.audioPermission = 'denied';
      this.saveSettings();
      this.notifyListeners();
      return false;
    }
  }

  async setSoundEnabled(enabled) {
    if (enabled && this.audioPermission !== 'granted') {
      const granted = await this.requestAudioPermission();
      if (!granted) {
        return false;
      }
    }
    
    this.soundEnabled = enabled;
    this.saveSettings();
    this.updateNotificationSound();
    this.notifyListeners();
    return true;
  }

  setVolume(volume) {
    this.volume = volume;
    this.saveSettings();
    this.notifyListeners();
  }

  setSelectedSound(soundId) {
    this.selectedSound = soundId;
    this.saveSettings();
    this.notifyListeners();
  }

  setupPageVisibilityListener() {
    // Bind methods for proper cleanup
    this.handleVisibilityChange = () => {
      this.isPageVisible = !document.hidden;
      
      // Continue sound notifications even when page is hidden
      // This allows notifications to work in background tabs
    };
    
    this.handleWindowFocus = () => {
      this.isPageVisible = true;
    };
    
    this.handleWindowBlur = () => {
      this.isPageVisible = false;
    };
    
    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Also listen for window focus/blur as backup
    window.addEventListener('focus', this.handleWindowFocus);
    window.addEventListener('blur', this.handleWindowBlur);
  }

  startOrderMonitoring() {
    if (this.orderMonitoringStarted) return;
    this.orderMonitoringStarted = true;

    import('./DataSyncService').then(({ default: dataSyncService }) => {
      this.dataSyncUnsubscribe = dataSyncService.subscribe('orders', (ordersData) => {
        this._handleOrdersUpdate(ordersData);
      });
    });
  }

  _handleOrdersUpdate(ordersData) {
    const pendingOrders = (ordersData?.all || []).filter(o =>
      ['unpaid', 'nieuw', 'paid', 'marked_as_paid'].includes(o.status)
    );
    const newPendingCount = pendingOrders.length;
    const oldPendingCount = this.pendingOrders;

    this.pendingOrders = newPendingCount;
    this.updateNotificationSound();

    if (newPendingCount !== oldPendingCount) {
      this.notifyListeners();
    }
  }

  createNotificationSound() {
    if (!this.audioContext) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.audioContext = ctx;
      return ctx;
    }
    return this.audioContext;
  }

  playNotificationBeep() {
    if (this.audioPermission !== 'granted') return;
    
    // Notification beep disabled: keep function for API compatibility but do not play any sound
    return;
  }


  playAudioFile(soundOption) {
    try {
      // Get or create audio element
      if (!this.audioElements[soundOption.id]) {
        const audio = new Audio(soundOption.file);
        audio.preload = 'auto';
        
        // Allow audio to play in background tabs
        audio.setAttribute('crossorigin', 'anonymous');
        
        this.audioElements[soundOption.id] = audio;
      }
      
      const audio = this.audioElements[soundOption.id];
      audio.volume = this.volume / 100;
      audio.currentTime = 0; // Reset to start
      
      
      // Play with error handling - works in background tabs
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
          })
          .catch(error => {
            console.error('NotificationService: Error playing audio file:', error);
            // Fallback to generated beep
            this.playGeneratedBeep();
          });
      }
    } catch (error) {
      console.error('NotificationService: Error with audio file:', error);
      // Fallback to generated beep
      this.playGeneratedBeep();
    }
  }

  playGeneratedBeep() {
    const ctx = this.createNotificationSound();
    if (!ctx) return;

    // Create a simple beep sound
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(800, ctx.currentTime); // 800Hz beep
    gainNode.gain.setValueAtTime(this.volume / 100 * 0.3, ctx.currentTime); // Adjust volume
    
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.2); // 200ms beep
  }

  updateNotificationSound() {

    // Stop any existing interval first
    this.stopNotificationSound();

    // Only start sound when enabled, we have permission, and there are pending orders
    if (this.soundEnabled && this.audioPermission === 'granted' && this.pendingOrders > 0) {
      this.startNotificationSound();
    } else {
    }
  }

  startNotificationSound() {
    if (this.notificationInterval) {
      return; // Already running
    }
    if (this.audioPermission !== 'granted') {
      return; // No permission
    }
    
    
    // Play immediately
    this.playNotificationBeep();
    
    // Then repeat every 5 seconds - but only play once regardless of order count
    this.notificationInterval = setInterval(() => {
      if (this.pendingOrders > 0 && this.soundEnabled && this.audioPermission === 'granted') {
        this.playNotificationBeep(); // Play once, not per order
      }
    }, 5000);
  }

  stopNotificationSound() {
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
      this.notificationInterval = null;
    }
  }

  // Force refresh of pending orders (call when order is accepted)
  async refreshPendingOrders() {
    await this.checkPendingOrders();
  }

  // Cleanup subscriptions
  cleanup() {
    if (this.dataSyncUnsubscribe) {
      this.dataSyncUnsubscribe();
      this.dataSyncUnsubscribe = null;
    }

    this.orderMonitoringStarted = false;
    this.stopNotificationSound();
    
    // Cleanup page visibility listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
    window.removeEventListener('blur', this.handleWindowBlur);
  }

  // Test sound function
  testSound(soundId = null) {
    if (soundId) {
      // Test specific sound
      const originalSound = this.selectedSound;
      this.selectedSound = soundId;
      this.playNotificationBeep();
      this.selectedSound = originalSound;
    } else {
      // Test current selected sound
      this.playNotificationBeep();
    }
  }
}

export default NotificationService;
